import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { IonModal, IonIcon, IonAvatar } from "@ionic/react";
import { callOutline, callSharp, micCircle, micOffCircle } from "ionicons/icons";
import { socketUrl, decodedAuthToken } from "../../../services/socketUrl";
import "./CallProvider.scss";
import { iceServers } from "../../../services/telephonyConfig";

/**
 * Peer-to-peer WebRTC voice calling.
 *
 * Audio flows directly browser-to-browser; the server only relays the tiny SDP
 * offer/answer + ICE candidates over the ws/call/ signaling socket. This is the
 * only calling approach that works within the single open port (443, via the WS
 * proxy) without a media server, and it is near-zero load on the VPS.
 *
 * Routing key is the peer's guid, which every user record the client holds
 * already carries (userData.guid).
 */

type CallState = "idle" | "outgoing" | "incoming" | "connected";

interface CallContextValue {
  /** Start an outgoing call to the peer identified by their guid. */
  startCall: (peerGuid: string, displayName?: string) => void;
  callState: CallState;
}

const CallContext = createContext<CallContextValue>({
  startCall: () => {},
  callState: "idle",
});

export const useCall = () => useContext(CallContext);

// Google's public STUN servers are unreachable on a private network, and
// gathering stalls there instead of failing fast. Resolved from window._env_
// so a site can point at its own coturn; see services/telephonyConfig.
const STUN_SERVERS = (): RTCIceServer[] => iceServers();

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [callState, setCallState] = useState<CallState>("idle");
  const [peerName, setPeerName] = useState<string>("");
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [statusText, setStatusText] = useState<string>("");

  const socketRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const peerGuidRef = useRef<string | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const pendingOfferRef = useRef<RTCSessionDescriptionInit | null>(null);
  const callStateRef = useRef<CallState>("idle");

  const setState = (s: CallState) => {
    callStateRef.current = s;
    setCallState(s);
  };

  const send = (msg: any) => {
    const sock = socketRef.current;
    if (sock && sock.readyState === WebSocket.OPEN) {
      sock.send(JSON.stringify(msg));
    }
  };

  const cleanup = useCallback(() => {
    try {
      pcRef.current?.getSenders().forEach((s) => s.track?.stop());
      pcRef.current?.close();
    } catch (e) {
      /* ignore */
    }
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pendingCandidatesRef.current = [];
    pendingOfferRef.current = null;
    peerGuidRef.current = null;
    setIsMuted(false);
    setStatusText("");
    setState("idle");
  }, []);

  const buildPeerConnection = useCallback((): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS() });
    pc.onicecandidate = (e) => {
      if (e.candidate && peerGuidRef.current) {
        send({
          type: "ice-candidate",
          to: peerGuidRef.current,
          candidate: e.candidate.toJSON(),
        });
      }
    };
    pc.ontrack = (e) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = e.streams[0];
        remoteAudioRef.current.play().catch(() => {});
      }
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected") {
        setState("connected");
        setStatusText("Connected");
      } else if (st === "failed" || st === "disconnected" || st === "closed") {
        if (callStateRef.current !== "idle") {
          if (peerGuidRef.current)
            send({ type: "call-end", to: peerGuidRef.current });
          cleanup();
        }
      }
    };
    return pc;
  }, [cleanup]);

  const getMic = async (): Promise<MediaStream> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });
    localStreamRef.current = stream;
    return stream;
  };

  const flushPendingCandidates = async () => {
    const pc = pcRef.current;
    if (!pc) return;
    for (const c of pendingCandidatesRef.current) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      } catch (e) {
        /* ignore */
      }
    }
    pendingCandidatesRef.current = [];
  };

  // ---- Outgoing ----
  const startCall = useCallback(
    async (peerGuid: string, displayName?: string) => {
      if (!peerGuid || callStateRef.current !== "idle") return;
      peerGuidRef.current = String(peerGuid);
      setPeerName(displayName || "");
      setStatusText("Calling…");
      setState("outgoing");
      try {
        const stream = await getMic();
        const pc = buildPeerConnection();
        pcRef.current = pc;
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));
        const offer = await pc.createOffer({ offerToReceiveAudio: true });
        await pc.setLocalDescription(offer);
        send({
          type: "call-offer",
          to: peerGuidRef.current,
          sdp: offer,
          from_name: displayName || "",
        });
      } catch (e) {
        setStatusText("Microphone unavailable");
        setTimeout(cleanup, 1500);
      }
    },
    [buildPeerConnection, cleanup]
  );

  // ---- Incoming accept ----
  const acceptCall = async () => {
    const offer = pendingOfferRef.current;
    const targetGuid = peerGuidRef.current;
    if (!offer || !targetGuid) return;
    setStatusText("Connecting…");
    try {
      const stream = await getMic();
      const pc = buildPeerConnection();
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      await flushPendingCandidates();
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ type: "call-answer", to: targetGuid, sdp: answer });
      setState("connected");
    } catch (e) {
      send({ type: "call-reject", to: targetGuid });
      cleanup();
    }
  };

  const rejectCall = () => {
    if (peerGuidRef.current) send({ type: "call-reject", to: peerGuidRef.current });
    cleanup();
  };

  const hangUp = () => {
    if (peerGuidRef.current) send({ type: "call-end", to: peerGuidRef.current });
    cleanup();
  };

  const toggleMute = () => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setIsMuted(!track.enabled);
    }
  };

  // ---- Signaling message handling ----
  const handleSignal = useCallback(
    async (data: any) => {
      const fromGuid: string | undefined = data.from_guid;
      const type: string = data.type;

      if (type === "call-offer") {
        // Busy: already in a call -> tell caller.
        if (callStateRef.current !== "idle") {
          if (fromGuid) send({ type: "call-busy", to: fromGuid });
          return;
        }
        peerGuidRef.current = fromGuid ?? null;
        pendingOfferRef.current = data.sdp;
        setPeerName(data.from_name || "");
        setStatusText("Incoming call…");
        setState("incoming");
        return;
      }

      if (type === "call-answer") {
        const pc = pcRef.current;
        if (pc && data.sdp) {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
            await flushPendingCandidates();
            setStatusText("Connecting…");
          } catch (e) {
            /* ignore */
          }
        }
        return;
      }

      if (type === "ice-candidate" && data.candidate) {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription && pc.remoteDescription.type) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch (e) {
            /* ignore */
          }
        } else {
          pendingCandidatesRef.current.push(data.candidate);
        }
        return;
      }

      if (type === "call-reject") {
        setStatusText("Call declined");
        setTimeout(cleanup, 1200);
        return;
      }
      if (type === "call-busy") {
        setStatusText("User is busy");
        setTimeout(cleanup, 1500);
        return;
      }
      if (type === "call-end") {
        setStatusText("Call ended");
        setTimeout(cleanup, 800);
        return;
      }
    },
    [cleanup]
  );

  // ---- Persistent signaling socket ----
  useEffect(() => {
    let stopped = false;
    let retry: any = null;

    const connect = () => {
      const token = decodedAuthToken();
      if (!token) {
        retry = setTimeout(connect, 4000);
        return;
      }
      const ws = new WebSocket(socketUrl("ws/call/", token));
      socketRef.current = ws;
      ws.onmessage = (ev) => {
        let data: any;
        try {
          data = JSON.parse(ev.data);
        } catch (e) {
          return;
        }
        if (data.type === "pong") return;
        handleSignal(data);
      };
      ws.onclose = () => {
        socketRef.current = null;
        if (!stopped) retry = setTimeout(connect, 4000);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {
          /* ignore */
        }
      };
    };
    connect();

    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      try {
        socketRef.current?.close();
      } catch (e) {
        /* ignore */
      }
    };
  }, [handleSignal]);

  const showModal = callState !== "idle";

  return (
    <CallContext.Provider value={{ startCall, callState }}>
      {children}
      <audio ref={remoteAudioRef} autoPlay />
      <IonModal isOpen={showModal} backdropDismiss={false} className="webrtc-call-modal">
        <div className="webrtc-call-container">
          <IonAvatar className="webrtc-call-avatar">
            <div className="webrtc-call-avatar-initial">
              {(peerName || "?").charAt(0).toUpperCase()}
            </div>
          </IonAvatar>
          <div className="webrtc-call-name">{peerName || "Unknown"}</div>
          <div className="webrtc-call-status">{statusText}</div>

          <div className="webrtc-call-actions">
            {callState === "incoming" ? (
              <>
                <button
                  className="webrtc-btn webrtc-btn-accept"
                  onClick={acceptCall}
                  aria-label="Accept"
                >
                  <IonIcon icon={callOutline} />
                </button>
                <button
                  className="webrtc-btn webrtc-btn-reject"
                  onClick={rejectCall}
                  aria-label="Reject"
                >
                  <IonIcon icon={callSharp} />
                </button>
              </>
            ) : (
              <>
                {callState === "connected" && (
                  <button
                    className="webrtc-btn webrtc-btn-mute"
                    onClick={toggleMute}
                    aria-label="Mute"
                  >
                    <IonIcon icon={isMuted ? micOffCircle : micCircle} />
                  </button>
                )}
                <button
                  className="webrtc-btn webrtc-btn-reject"
                  onClick={hangUp}
                  aria-label="Hang up"
                >
                  <IonIcon icon={callSharp} />
                </button>
              </>
            )}
          </div>
        </div>
      </IonModal>
    </CallContext.Provider>
  );
};

export default CallProvider;
